import React from "react";
import { useTranslation } from "react-i18next";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@shadcn-in-nx/ui/table";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@shadcn-in-nx/ui/dropdown-menu";
import { Button } from "@shadcn-in-nx/ui/button";
import {
    MoreHorizontal, Trash2, File, Download, Image as ImageIcon, FileText,
} from "lucide-react";
import { formatDate } from "@utils";

const getFileIcon = (type) => {
    if (!type) return <File className="h-4 w-4" />;
    if (type.startsWith("image/")) return <ImageIcon className="h-4 w-4" />;
    if (type.includes("pdf") || type.includes("word") || type.includes("document"))
        return <FileText className="h-4 w-4" />;
    return <File className="h-4 w-4" />;
};

const formatFileSize = (bytes) => {
    if (!bytes) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
};

const getFriendlyFileType = (mimeType) => {
    if (!mimeType) return "File";
    if (mimeType.startsWith("image/")) return "Image";
    if (mimeType.includes("pdf")) return "PDF Document";
    if (mimeType.includes("word") || mimeType.includes("document")) return "Word Document";
    if (mimeType.includes("excel") || mimeType.includes("spreadsheet")) return "Excel Spreadsheet";
    if (mimeType.includes("powerpoint") || mimeType.includes("presentation")) return "PowerPoint";
    if (mimeType.includes("text")) return "Text File";
    if (mimeType.includes("zip") || mimeType.includes("rar") || mimeType.includes("tar")) return "Archive";
    if (mimeType.startsWith("video/")) return "Video";
    if (mimeType.startsWith("audio/")) return "Audio";
    if (mimeType.includes("json")) return "JSON File";
    if (mimeType.includes("csv")) return "CSV File";
    return "File";
};

function FilesTable({ files, searchTerm, onDownload, onDelete }) {
    const { t } = useTranslation();

    if (files.length === 0) {
        return (
            <div className="text-center p-8 rounded-lg border-2 border-dashed border-gray-300 bg-white">
                <div className="flex flex-col items-center gap-2">
                    <div className="rounded-full bg-gray-50 p-3">
                        <File className="h-8 w-8 text-gray-400" />
                    </div>
                    <h3 className="mt-2 text-sm font-semibold text-gray-900">
                        {searchTerm
                            ? t("files.emptyState.noResults", { searchTerm })
                            : t("files.emptyState.noFiles")}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500 max-w-sm">
                        {searchTerm
                            ? t("files.emptyState.noResultsDescription")
                            : t("files.emptyState.noFilesDescription")}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead>{t("files.table.name")}</TableHead>
                    <TableHead className="hidden sm:table-cell">{t("files.table.type")}</TableHead>
                    <TableHead className="hidden md:table-cell">{t("files.table.size")}</TableHead>
                    <TableHead className="hidden md:table-cell">{t("files.table.uploadedBy")}</TableHead>
                    <TableHead className="hidden md:table-cell">{t("files.table.workflow")}</TableHead>
                    <TableHead className="hidden lg:table-cell">{t("files.table.created")}</TableHead>
                    <TableHead className="w-1">{t("files.table.actions")}</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {files.map((file) => (
                    <TableRow key={file.id}>
                        <TableCell>
                            <div className="flex items-center gap-2">
                                {getFileIcon(file.type)}
                                <div className="flex flex-col">
                                    <span className="text-sm font-medium">{file.name}</span>
                                    <div className="sm:hidden flex flex-wrap gap-2 text-xs text-gray-500 mt-1">
                                        <span>{getFriendlyFileType(file.type)}</span>
                                        <span>•</span>
                                        <span>{formatFileSize(file.size)}</span>
                                    </div>
                                </div>
                            </div>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">{getFriendlyFileType(file.type)}</TableCell>
                        <TableCell className="hidden md:table-cell">{formatFileSize(file.size)}</TableCell>
                        <TableCell className="hidden md:table-cell">
                            {file.user
                                ? `${file.user.firstName || ""} ${file.user.lastName || ""}`.trim() || t("files.table.unknown")
                                : t("files.table.unknown")}
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                            {file.workflows?.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                    {file.workflows.map((wf) => (
                                        <span key={wf.workflow.id} className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                                            {wf.workflow.name}
                                        </span>
                                    ))}
                                </div>
                            ) : (
                                <span className="text-gray-400 text-xs">—</span>
                            )}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">{formatDate(new Date(file.createdAt))}</TableCell>
                        <TableCell className="text-right">
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" className="h-8 w-8 p-0">
                                        <span className="sr-only">{t("files.table.openMenu")}</span>
                                        <MoreHorizontal className="h-4 w-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => onDownload(file)}>
                                        <Download className="mr-2 h-4 w-4" />
                                        <span>{t("files.actions.download")}</span>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => onDelete(file)} className="text-red-600">
                                        <Trash2 className="mr-2 h-4 w-4" />
                                        <span>{t("files.actions.delete")}</span>
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}

export default FilesTable;
